/**
 * Face Liveness Check Component
 * =============================
 * Cloud Face Liveness integration using Amplify FaceLivenessDetector.
 *
 * Flow:
 * 1. Creates a liveness session via our backend
 * 2. Renders AWS Amplify FaceLivenessDetector
 * 3. User completes liveness challenge
 * 4. Fetches results from backend
 * 5. Optionally triggers face matching with ID document
 *
 * Requires AWS credentials configured for the frontend.
 * Set REACT_APP_AWS_REGION in environment variables.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Amplify } from 'aws-amplify';
import { FaceLivenessDetector } from '@aws-amplify/ui-react-liveness';
import '@aws-amplify/ui-react/styles.css';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Camera,
  Shield,
  Scan,
  User,
} from 'lucide-react';
import BiometricsService from '../../services/biometrics';

// Configure Amplify with Cognito Identity Pool
// Required for FaceLivenessDetector to get temporary AWS credentials
const identityPoolId = process.env.REACT_APP_AWS_IDENTITY_POOL_ID;

// Extract region from identity pool ID (format: us-east-2:uuid)
// Falls back to REACT_APP_AWS_REGION or us-east-1
const extractRegionFromPoolId = (poolId) => {
  if (poolId && poolId.includes(':')) {
    return poolId.split(':')[0];
  }
  return null;
};

const awsRegion = extractRegionFromPoolId(identityPoolId)
  || process.env.REACT_APP_AWS_REGION
  || 'us-east-1';

if (identityPoolId) {
  Amplify.configure({
    Auth: {
      Cognito: {
        identityPoolId: identityPoolId,
        region: awsRegion,
        allowGuestAccess: true,
      },
    },
  });
}

// Check if properly configured
const isAwsConfigured = Boolean(identityPoolId);

// Status colors
const STATUS_COLORS = {
  success: '#10b981',
  error: '#ef4444',
  warning: '#f59e0b',
  info: '#3b82f6',
  neutral: '#6b7280',
};

// Card component
const Card = ({ children, style = {} }) => (
  <div
    style={{
      background: '#1a1a2e',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: '12px',
      padding: '24px',
      ...style,
    }}
  >
    {children}
  </div>
);

// Status Badge
const StatusBadge = ({ status, confidence }) => {
  const configs = {
    completed: { color: STATUS_COLORS.success, icon: CheckCircle2, label: 'Live' },
    failed: { color: STATUS_COLORS.error, icon: XCircle, label: 'Failed' },
    created: { color: STATUS_COLORS.info, icon: Loader2, label: 'Ready' },
    in_progress: { color: STATUS_COLORS.warning, icon: Scan, label: 'Scanning' },
    expired: { color: STATUS_COLORS.neutral, icon: AlertTriangle, label: 'Expired' },
  };

  const config = configs[status] || configs.created;
  const Icon = config.icon;

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 16px',
        background: `${config.color}20`,
        borderRadius: '20px',
        color: config.color,
        fontWeight: 600,
        fontSize: '14px',
      }}
    >
      <Icon size={18} className={status === 'in_progress' ? 'animate-spin' : ''} />
      {config.label}
      {confidence && (
        <span style={{ opacity: 0.8 }}>({confidence.toFixed(1)}%)</span>
      )}
    </div>
  );
};

// Main Component
const FaceLivenessCheck = ({
  applicantId,
  onComplete,
  onError,
  autoStart = false,
  showFaceMatch = true,
}) => {
  const [state, setState] = useState('idle'); // idle, loading, ready, detecting, results, error
  const [sessionData, setSessionData] = useState(null);
  const [results, setResults] = useState(null);
  const [faceMatchResults, setFaceMatchResults] = useState(null);
  const [error, setError] = useState(null);

  // Create a new liveness session
  const createSession = useCallback(async () => {
    try {
      setState('loading');
      setError(null);

      const response = await BiometricsService.createLivenessSession(applicantId);

      if (response.data) {
        setSessionData(response.data);
        setState('ready');
      } else {
        throw new Error('Failed to create liveness session');
      }
    } catch (err) {
      console.error('Error creating liveness session:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to create session');
      setState('error');
      onError?.(err);
    }
  }, [applicantId, onError]);

  // Fetch results after liveness check completes
  const fetchResults = useCallback(async () => {
    if (!sessionData) return;

    try {
      setState('loading');
      const response = await BiometricsService.getLivenessSessionResults(sessionData.session_id);

      if (response.data) {
        setResults(response.data);
        setState('results');

        // If liveness passed and face match is enabled, trigger it
        if (response.data.is_live && showFaceMatch) {
          try {
            const matchResponse = await BiometricsService.matchLivenessToId(sessionData.session_id);
            setFaceMatchResults(matchResponse.data);
          } catch (matchErr) {
            console.error('Face match error:', matchErr);
            // Don't fail the whole flow if face match fails
          }
        }

        onComplete?.(response.data);
      }
    } catch (err) {
      console.error('Error fetching liveness results:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to get results');
      setState('error');
      onError?.(err);
    }
  }, [sessionData, showFaceMatch, onComplete, onError]);

  // Auto-start on mount if enabled
  useEffect(() => {
    if (autoStart && state === 'idle') {
      createSession();
    }
  }, [autoStart, state, createSession]);

  // Handle liveness analysis complete
  const handleAnalysisComplete = async () => {
    setState('loading');
    // Small delay to ensure AWS has processed the results
    await new Promise((resolve) => setTimeout(resolve, 1500));
    await fetchResults();
  };

  // Handle liveness error
  const handleError = (err) => {
    console.error('FaceLivenessDetector error:', err);
    setError(err?.message || 'Liveness detection failed');
    setState('error');
    onError?.(err);
  };

  // Render based on state
  if (state === 'idle') {
    // Show configuration warning if Cognito isn't set up
    if (!isAwsConfigured) {
      return (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: `${STATUS_COLORS.warning}20`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
              }}
            >
              <AlertTriangle size={36} color={STATUS_COLORS.warning} />
            </div>
            <h3 style={{ color: 'white', marginBottom: '12px', fontSize: '20px' }}>
              AWS Configuration Required
            </h3>
            <p style={{ color: '#9ca3af', marginBottom: '16px', lineHeight: 1.6 }}>
              Face Liveness requires AWS Cognito Identity Pool to be configured.
            </p>
            <div
              style={{
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '8px',
                padding: '16px',
                textAlign: 'left',
                fontSize: '13px',
                color: '#9ca3af',
              }}
            >
              <p style={{ marginBottom: '8px', fontWeight: 600, color: 'white' }}>Add to Vercel:</p>
              <code style={{ color: '#60a5fa' }}>
                REACT_APP_AWS_IDENTITY_POOL_ID=us-east-1:xxxx...
              </code>
            </div>
          </div>
        </Card>
      );
    }

    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div
            style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #3b82f6 0%, var(--accent-secondary) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
            }}
          >
            <Camera size={36} color="white" />
          </div>
          <h3 style={{ color: 'white', marginBottom: '12px', fontSize: '20px' }}>
            Face Liveness Verification
          </h3>
          <p style={{ color: '#9ca3af', marginBottom: '24px', lineHeight: 1.6 }}>
            Complete a quick liveness check to verify you're a real person.
            This helps prevent fraud and protects your identity.
          </p>
          <button
            onClick={createSession}
            style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, var(--accent-secondary) 100%)',
              color: 'white',
              border: 'none',
              padding: '14px 32px',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Scan size={18} />
            Start Verification
          </button>
        </div>
      </Card>
    );
  }

  if (state === 'loading') {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <Loader2
            size={48}
            color="#3b82f6"
            style={{ animation: 'spin 1s linear infinite' }}
          />
          <p style={{ color: '#9ca3af', marginTop: '20px' }}>
            {sessionData ? 'Processing results...' : 'Creating liveness session...'}
          </p>
        </div>
      </Card>
    );
  }

  if (state === 'error') {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div
            style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: `${STATUS_COLORS.error}20`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
            }}
          >
            <XCircle size={36} color={STATUS_COLORS.error} />
          </div>
          <h3 style={{ color: 'white', marginBottom: '12px', fontSize: '20px' }}>
            Verification Failed
          </h3>
          <p style={{ color: '#9ca3af', marginBottom: '24px' }}>{error}</p>
          <button
            onClick={createSession}
            style={{
              background: 'rgba(255,255,255,0.1)',
              color: 'white',
              border: '1px solid rgba(255,255,255,0.2)',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <RefreshCw size={16} />
            Try Again
          </button>
        </div>
      </Card>
    );
  }

  if (state === 'ready' && sessionData) {
    return (
      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '16px 24px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Scan size={20} color="#3b82f6" />
            <span style={{ color: 'white', fontWeight: 600 }}>Face Liveness Check</span>
            <StatusBadge status="created" />
          </div>
          <p style={{ color: '#9ca3af', fontSize: '13px', marginTop: '8px' }}>
            Follow the on-screen instructions. Move your face as directed.
          </p>
        </div>
        <div style={{ minHeight: '500px' }}>
          <FaceLivenessDetector
            sessionId={sessionData.aws_session_id}
            region={process.env.REACT_APP_AWS_REGION || 'us-east-1'}
            onAnalysisComplete={handleAnalysisComplete}
            onError={handleError}
            disableStartScreen={false}
            components={{
              PhotosensitiveWarning: () => (
                <div style={{ padding: '16px', background: '#292945', borderRadius: '8px', margin: '16px' }}>
                  <AlertTriangle size={20} color="#f59e0b" style={{ marginBottom: '8px' }} />
                  <p style={{ color: '#f59e0b', fontSize: '14px', margin: 0 }}>
                    This check includes flashing colors. If you are sensitive to flashing lights,
                    please proceed with caution or contact support for alternatives.
                  </p>
                </div>
              ),
            }}
          />
        </div>
      </Card>
    );
  }

  if (state === 'results' && results) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          {/* Result Icon */}
          <div
            style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: results.is_live
                ? `${STATUS_COLORS.success}20`
                : `${STATUS_COLORS.error}20`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
            }}
          >
            {results.is_live ? (
              <CheckCircle2 size={48} color={STATUS_COLORS.success} />
            ) : (
              <XCircle size={48} color={STATUS_COLORS.error} />
            )}
          </div>

          {/* Result Title */}
          <h3 style={{ color: 'white', marginBottom: '8px', fontSize: '24px' }}>
            {results.is_live ? 'Liveness Verified!' : 'Verification Failed'}
          </h3>
          <StatusBadge status={results.status} confidence={results.confidence} />

          {/* Confidence Score */}
          <div
            style={{
              marginTop: '32px',
              padding: '20px',
              background: 'rgba(255,255,255,0.05)',
              borderRadius: '12px',
            }}
          >
            <div style={{ marginBottom: '16px' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '8px',
                }}
              >
                <span style={{ color: '#9ca3af', fontSize: '14px' }}>Liveness Confidence</span>
                <span
                  style={{
                    color: results.confidence >= 90 ? STATUS_COLORS.success : STATUS_COLORS.warning,
                    fontWeight: 600,
                  }}
                >
                  {results.confidence?.toFixed(1)}%
                </span>
              </div>
              <div
                style={{
                  height: '8px',
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '4px',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${results.confidence || 0}%`,
                    height: '100%',
                    background:
                      results.confidence >= 90
                        ? STATUS_COLORS.success
                        : results.confidence >= 70
                        ? STATUS_COLORS.warning
                        : STATUS_COLORS.error,
                    borderRadius: '4px',
                    transition: 'width 0.5s ease-out',
                  }}
                />
              </div>
            </div>

            {/* Face Match Results */}
            {faceMatchResults && (
              <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <User size={16} color="#9ca3af" />
                  <span style={{ color: '#9ca3af', fontSize: '14px' }}>Face Match with ID</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: 'white', fontWeight: 600 }}>
                    {faceMatchResults.match ? 'Matched' : 'Not Matched'}
                  </span>
                  <span
                    style={{
                      color: faceMatchResults.similarity >= 90 ? STATUS_COLORS.success : STATUS_COLORS.warning,
                      fontWeight: 600,
                    }}
                  >
                    {faceMatchResults.similarity?.toFixed(1)}% similar
                  </span>
                </div>
                {faceMatchResults.is_mock && (
                  <p style={{ color: '#f59e0b', fontSize: '12px', marginTop: '8px' }}>
                    (Mock data - configure AWS for real verification)
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Mock Warning */}
          {results.is_mock && (
            <div
              style={{
                marginTop: '24px',
                padding: '12px 16px',
                background: `${STATUS_COLORS.warning}15`,
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <AlertTriangle size={16} color={STATUS_COLORS.warning} />
              <span style={{ color: STATUS_COLORS.warning, fontSize: '13px' }}>
                Mock mode - Configure AWS credentials for production
              </span>
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ marginTop: '32px', display: 'flex', gap: '12px', justifyContent: 'center' }}>
            {!results.is_live && (
              <button
                onClick={createSession}
                style={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, var(--accent-secondary) 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <RefreshCw size={16} />
                Try Again
              </button>
            )}
            {results.is_live && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: STATUS_COLORS.success,
                }}
              >
                <Shield size={20} />
                <span style={{ fontWeight: 600 }}>Identity Verified</span>
              </div>
            )}
          </div>
        </div>
      </Card>
    );
  }

  return null;
};

export default FaceLivenessCheck;
